import { IconChevronDown, IconImage } from 'components/icons';
import VideoAndImageList from 'components/shared/video-and-image-list';
import { ApartmentOutput, CommercialAreaOutput, TownhouseOutput } from 'graphql/main/queries';
import { useTranslation } from 'hooks';
import { filter, includes, isEmpty, isEqual, isNil, join, map, toString } from 'lodash';
import { Fragment, useState } from 'react';
import { convertNumberToVietnameseCurrencyShortString } from '../../../../../../../utils';

export type RealEstateType =
  | 'apartment'
  | 'independentVilla'
  | 'townhouse'
  | 'doubleVilla'
  | 'shophouse'
  | 'condotel'
  | 'offictel'
  | 'penthouse'
  | 'commercialArea';
interface Props {
  realEstates?: TownhouseOutput[] | ApartmentOutput[] | CommercialAreaOutput[];
  type?: RealEstateType;
}

const RealEstateInformation = ({ realEstates, type }: Props) => {
  const translate = useTranslation();
  const [isExpandAccordion, setExpandAccordion] = useState(false);
  const convertedRealEstate = realEstates as TownhouseOutput[];

  const toggleExpandAccordion = () => {
    setExpandAccordion(!isExpandAccordion);
  };

  return !isEmpty(convertedRealEstate) ? (
    <div
      id='real-estate-information'
      className={`rounded-[8px] p-[16px] shadow-25 duration-[200ms] ${
        isExpandAccordion ? '' : 'ease-in-out hover:scale-[1.01]'
      }`}
    >
      <div
        className='flex h-[40px] cursor-pointer items-center justify-between rounded-[8px] bg-secondary px-[12px] text-primary'
        onClick={toggleExpandAccordion}
      >
        <h2 className='cursor-pointer select-none text-[16px] font-[600] leading-[20px]'>
          Thông tin {(translate.desktop.detailProject.realEstate as any)[toString(type)]}
        </h2>
        <div className='flex cursor-pointer select-none items-center text-primary'>
          <span className='text-[12px] leading-[15px]'>
            {isExpandAccordion ? 'Ẩn bớt' : 'Xem thêm'}
          </span>
          <IconChevronDown
            className={`ml-[6px] h-[15px] w-[15px] font-[800] ${
              isExpandAccordion && 'rotate-180'
            } duration-[200ms]`}
          />
        </div>
      </div>
      <div className={`${isExpandAccordion ? 'max-fit' : 'max-h-0'} divide-y overflow-hidden`}>
        {map(convertedRealEstate, (realEstate, realEstateIndex) => {
          const overviewProperties = filter(
            [
              {
                type: 'width',
                value: realEstate.width ? realEstate.width.toLocaleString() : undefined,
                suffix: '(m)',
              },
              {
                type: 'length',
                value: realEstate.length ? realEstate.length.toLocaleString() : undefined,
                suffix: '(m)',
              },
              {
                type: 'area',
                value: realEstate.area ? realEstate.area.toLocaleString() : undefined,
                suffix: '(m\u00B2)',
              },
              {
                type: 'price',
                value: realEstate.price
                  ? convertNumberToVietnameseCurrencyShortString(realEstate.price)
                  : undefined,
                suffix: '(VND/m\u00B2)',
              },
            ],
            (property) => !isNil(property.value),
          );
          const detailInformations = filter(
            [
              {
                type: 'bedroomCount',
                value:
                  realEstate.bedroomCount?.from && realEstate.bedroomCount?.to
                    ? `${realEstate.bedroomCount?.from} - ${realEstate.bedroomCount?.to}`
                    : realEstate.bedroomCount?.from
                    ? realEstate.bedroomCount?.from
                    : realEstate.bedroomCount?.to
                    ? realEstate.bedroomCount?.to
                    : undefined,
              },
              {
                type: 'floorCount',
                value:
                  realEstate.floorCount?.from && realEstate.floorCount?.to
                    ? `${realEstate.floorCount?.from} - ${realEstate.floorCount?.to}`
                    : realEstate.floorCount?.from
                    ? realEstate.floorCount?.from
                    : realEstate.floorCount?.to
                    ? realEstate.floorCount?.to
                    : undefined,
              },
              {
                type: 'kioskCount',
                value:
                  (realEstate as CommercialAreaOutput).kioskCount?.from &&
                  (realEstate as CommercialAreaOutput).kioskCount?.to
                    ? `${(realEstate as CommercialAreaOutput).kioskCount?.from} - ${(
                        realEstate as CommercialAreaOutput
                      ).kioskCount?.to}`
                    : (realEstate as CommercialAreaOutput).kioskCount?.from
                    ? (realEstate as CommercialAreaOutput).kioskCount?.from
                    : (realEstate as CommercialAreaOutput).kioskCount?.to
                    ? (realEstate as CommercialAreaOutput).kioskCount?.to
                    : undefined,
              },
              {
                type: 'bedroomArea',
                value:
                  realEstate.bedroomArea?.from && realEstate.bedroomArea?.to
                    ? `${realEstate.bedroomArea?.from} - ${realEstate.bedroomArea?.to}`
                    : realEstate.bedroomArea?.from
                    ? realEstate.bedroomArea?.from
                    : realEstate.bedroomArea?.to
                    ? realEstate.bedroomArea?.to
                    : undefined,
                suffix: '(m\u00B2)',
              },
              {
                type: 'toiletCount',
                value:
                  realEstate.toiletCount?.from && realEstate.toiletCount?.to
                    ? `${realEstate.toiletCount?.from} - ${realEstate.toiletCount?.to}`
                    : realEstate.toiletCount?.from
                    ? realEstate.toiletCount?.from
                    : realEstate.toiletCount?.to
                    ? realEstate.toiletCount?.to
                    : undefined,
              },
              {
                type: 'toiletArea',
                value:
                  realEstate.toiletArea?.from && realEstate.toiletArea?.to
                    ? `${realEstate.toiletArea?.from} - ${realEstate.toiletArea?.to}`
                    : realEstate.toiletArea?.from
                    ? realEstate.toiletArea?.from
                    : realEstate.toiletArea?.to
                    ? realEstate.toiletArea?.to
                    : undefined,
                suffix: '(m\u00B2)',
              },
              {
                type: 'internalRoadArea',
                value:
                  realEstate.internalRoadArea?.from && realEstate.internalRoadArea?.to
                    ? `${realEstate.internalRoadArea?.from} - ${realEstate.internalRoadArea?.to}`
                    : realEstate.internalRoadArea?.from
                    ? realEstate.internalRoadArea?.from
                    : realEstate.internalRoadArea?.to
                    ? realEstate.internalRoadArea?.to
                    : undefined,
                suffix: '(m\u00B2)',
              },
            ],
            (detail) => !isNil(detail.value),
          );
          const priceByStages = filter(
            realEstate.priceByStages,
            (stage) => !isNil(stage.priceByStage),
          );
          return (
            <div key={realEstateIndex} className='mb-[24px] space-y-[24px]'>
              <div className='mt-[24px] flex flex-col space-y-[24px]'>
                <div className='flex items-center space-x-[10px]'>
                  <div className='flex h-[32px] w-[32px] items-center justify-center rounded-full bg-[#22c55e]'>
                    <span className='font-[600] text-paper'>{realEstateIndex + 1}</span>
                  </div>
                  <h2 className='text-[16px] font-[600] leading-[20px]'>
                    {realEstate.title ?? ''}
                  </h2>
                </div>
              </div>
              {!isEmpty(overviewProperties) ? (
                <ul className='m-[-8px] flex flex-wrap'>
                  {map(overviewProperties, (property, propertyIndex) => (
                    <li
                      key={`real-estate-property-${propertyIndex}`}
                      className='flex w-full items-center space-x-[32px] p-[8px] lg:basis-1/2'
                    >
                      <span className='flex-1 font-[600]'>
                        {(translate.desktop.detailProject.product as any)[property.type]}{' '}
                        {property.suffix}
                      </span>
                      <span
                        className={`flex-1 ${
                          isEqual(property.type, 'price') ? 'font-[600] text-alert' : ''
                        }`}
                      >
                        {property.value}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {!isEmpty(detailInformations) ? (
                <Fragment>
                  <h2 className='text-[16px] font-[600] leading-[20px] text-text2'>
                    {translate.desktop.detailProject.realEstate.detail}
                  </h2>
                  <ul className='m-[-8px] mt-[-16px] flex flex-wrap'>
                    {map(detailInformations, (detail, detailIndex) => (
                      <li
                        key={`real-estate-detail-${detailIndex}`}
                        className='flex w-full items-center space-x-[32px] p-[8px] lg:basis-1/2'
                      >
                        <span className='flex-1 font-[600]'>
                          {(translate.desktop.detailProject.realEstate as any)[detail.type]}{' '}
                          {includes(
                            ['bedroomCount', 'toiletCount', 'floorCount', 'kioskCount'],
                            detail.type,
                          )
                            ? (translate.desktop.detailProject.realEstate as any)[toString(type)]
                            : ''}
                          {detail.suffix ?? ''}
                        </span>
                        <span className='flex-1'>{detail.value}</span>
                      </li>
                    ))}
                  </ul>
                </Fragment>
              ) : null}
              {!isNil(realEstate.views) && !isEmpty(realEstate.views) && (
                <div className='!mt-[20px] flex space-x-[16px]'>
                  <span className='inline-block flex-1 font-[600]'>View</span>
                  <span className='inline-block flex-[3]'>
                    {join(
                      map(
                        realEstate.views,
                        (view) => (translate.major.villaViewTypes as any)[view],
                      ),
                      ', ',
                    )}
                  </span>
                </div>
              )}
              {!isNil(realEstate.imageAndVideos) && !isEmpty(realEstate.imageAndVideos) && (
                <div className='space-y-[16px]'>
                  <div className='flex items-center space-x-[10px]'>
                    <div className='flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-background'>
                      <IconImage className='h-[18px] w-[18px]' />
                    </div>
                    <h3 className='font-[600]'>Hình ảnh & Video căn hộ mẫu</h3>
                  </div>
                  <VideoAndImageList
                    id={`${type}-${realEstateIndex}`}
                    medias={realEstate.imageAndVideos}
                  />
                </div>
              )}
              {!isEmpty(priceByStages) ? (
                <div className='flex flex-col space-y-[16px]'>
                  <h2 className='font-[600] leading-[32px]'>Giá theo từng giai đoạn</h2>
                  <div className='flex'>
                    <span className='basis-1/2 font-[600]'>Giai đoạn</span>
                    <span className='basis-1/2 font-[600]'>Giá</span>
                  </div>
                  <div className='mt-[-8px] border-b border-stroke' />
                  {map(priceByStages, (stage, stageIndex) => (
                    <div key={`stage-price-${stageIndex}`} className='flex'>
                      <span className='basis-1/2'>{stage.name ?? ''}</span>
                      <span className='basis-1/2 font-[600] text-alert'>
                        {stage.priceByStage?.from && stage.priceByStage?.to
                          ? `${convertNumberToVietnameseCurrencyShortString(
                              stage.priceByStage?.from,
                            )} - ${convertNumberToVietnameseCurrencyShortString(
                              stage.priceByStage?.to,
                            )}/ m\u00B2`
                          : stage?.priceByStage?.from
                          ? `${convertNumberToVietnameseCurrencyShortString(
                              stage?.priceByStage.from,
                            )}/ m\u00B2`
                          : stage?.priceByStage?.to
                          ? `${convertNumberToVietnameseCurrencyShortString(
                              stage?.priceByStage.to,
                            )}/ m\u00B2`
                          : null}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;
};

export default RealEstateInformation;
